import React, { useEffect, useState } from 'react';
import { BookOpen, ChevronDown, ChevronRight, Check, MousePointerClick } from 'lucide-react';
import SetupTour, { tourSeen, TOUR_STEPS } from './SetupTour';

/**
 * The introduction shown above the team form: what a team is, how to set one
 * up, and how the email side works.
 *
 * The steps are not written out here any more. They are walked through on the
 * form itself by SetupTour, which darkens the page and points at the one box
 * each step is about. This card holds the parts that are worth reading rather
 * than following: what a team is, what the mailbox does, who is on the team.
 * Open by default when creating a team, folded away when editing one.
 */

// What is already filled in on the team in front of you.
const Chip = ({ done, children }) => (
  <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${done
    ? 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300'
    : 'border-gray-300 bg-white text-gray-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400'}`}>
    {done ? <Check className="h-3 w-3" /> : <span className="h-2 w-2 rounded-full border border-current" />}
    {children}
  </span>
);

// Who does what, and on which model. The fast model does the reading and
// sorting; the strong one writes anything an affiliate will see.
const WHO = [
  ['Scout', 'Searches the untapped pool for affiliates that do not promote the brand yet, and ranks them.', 'fast'],
  ['Vetter', 'Checks each one: markets, keywords, the site itself, whether you already work with them.', 'fast'],
  ['Contact agent', 'Writes the first email and the follow-ups.', 'strong'],
  ['Reply reader', 'Reads every reply and sorts it by intent.', 'fast'],
  ['Negotiator', 'Plans the next move: checks their claims against data, decides the offer and what to ask back.', 'strong'],
  ['Affiliate Manager', 'Writes each reply from that plan, and closes the deal.', 'strong'],
  ['Stop agent', 'When a deal stalls on terms, puts it on hold for a few days, then writes one check-in in the same thread.', 'strong'],
  ['Deal desk', 'Turns your description of a deal into exact saved terms.', 'fast'],
  ['Lesson learner', 'Turns your edits to its drafts into standing rules.', 'fast']
];

const SetupGuide = ({ team, mailboxes = [], readableMailbox, repliesReadable, brandChosen, dealsChosen, sendersChosen }) => {
  const [open, setOpen] = useState(!team);
  const [showEmail, setShowEmail] = useState(false);
  const [showWho, setShowWho] = useState(false);
  const [tour, setTour] = useState(false);

  // Opens itself the first time someone creates a team, and never again
  // unasked. The form has to be on the page before it can point at anything.
  useEffect(() => {
    if (team || tourSeen()) return undefined;
    const t = setTimeout(() => setTour(true), 400);
    return () => clearTimeout(t);
  }, [team]);

  return (
    <section className="rounded-xl border border-blue-200 bg-blue-50/60 dark:border-blue-900 dark:bg-blue-950/30">
      <button type="button" onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-5 py-3 text-left">
        <BookOpen className="h-4 w-4 flex-none text-blue-600 dark:text-blue-300" />
        <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">
          {team ? 'How this team works' : 'Setting up a team: start here'}
        </span>
        <span className="ml-auto text-blue-500 dark:text-blue-300">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      </button>

      {open && (
        <div className="space-y-4 px-5 pb-5">
          <p className="text-sm leading-relaxed text-blue-900/90 dark:text-blue-100/90">
            A team is a set of agents working one brand. They find affiliates who do not promote it yet, check that
            each one is a real fit, email them from your own mailbox, and negotiate inside the deals you saved —
            never beyond them. Nothing is sent until you press <strong>Start team</strong>, and you decide how much
            waits for your approval first.
          </p>

          <div className="rounded-lg border border-blue-200 bg-white/70 p-3 dark:border-blue-900 dark:bg-slate-900/50">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <button type="button" onClick={() => setTour(true)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-blue-700">
                <MousePointerClick className="h-4 w-4" />
                Walk me through it
              </button>
              <span className="text-xs text-gray-600 dark:text-slate-400">
                {TOUR_STEPS} steps on the form itself: it points at one box at a time and says what goes in it.
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-xs text-gray-500 dark:text-slate-400">Already done:</span>
              <Chip done={!!brandChosen}>Brand</Chip>
              <Chip done={!!dealsChosen}>Deal</Chip>
              <Chip done={!!sendersChosen}>Mailbox</Chip>
              <Chip done={!!repliesReadable}>Replies can be read</Chip>
              <Chip done={team?.status === 'active'}>Started</Chip>
            </div>
          </div>

          <div className="rounded-lg border border-blue-200 bg-white/70 dark:border-blue-900 dark:bg-slate-900/50">
            <button type="button" onClick={() => setShowEmail(!showEmail)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-blue-900 dark:text-blue-100">
              {showEmail ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              How the email side works
            </button>
            {showEmail && (
              <ul className="list-disc space-y-1.5 px-8 pb-3 text-xs leading-relaxed text-gray-700 dark:text-slate-300">
                <li><strong>Your mailbox, your reputation.</strong> Emails are sent over your own SMTP, from your own address. There is no shared sending domain, so deliverability is yours to keep — which is why the caps below exist.</li>
                <li><strong>One affiliate, one mailbox.</strong> A new affiliate is given the least busy mailbox on the team and keeps it for the whole thread, so replies always come back to the address that wrote.</li>
                <li><strong>Replies are read by IMAP</strong> every few minutes. Each one is matched to its conversation, sorted by intent, and answered after a random delay in the range you set, so nothing lands a second after they hit send.</li>
                <li><strong>Follow-ups stay in the same thread</strong>, counted from the previous email, and stop the moment they reply.</li>
                <li><strong>Pace is capped</strong> per mailbox per day and spaced out by a minimum gap, across every team sharing that mailbox. A mailbox sending hundreds of cold emails a day is a mailbox on its way to the spam folder.</li>
                <li><strong>Out-of-office, bounces and opt-outs</strong> are handled without a person: an absence notice waits, a hard bounce tries the next address on the affiliate, and anyone asking to be left alone is suppressed for the whole account.</li>
                <li><strong>An unsubscribe line is added</strong> to outreach automatically, and your company name and postal address have to be on your profile before a single email can go out.</li>
                <li><strong>Nothing is sent by a model.</strong> The agents write; code decides what goes, when and to whom — and anything over a deal limit or against a rule waits in Approvals.</li>
              </ul>
            )}
          </div>

          <div className="rounded-lg border border-blue-200 bg-white/70 dark:border-blue-900 dark:bg-slate-900/50">
            <button type="button" onClick={() => setShowWho(!showWho)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium text-blue-900 dark:text-blue-100">
              {showWho ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
              Who is on the team, and what it costs
            </button>
            {showWho && (
              <div className="px-3 pb-3">
                <table className="w-full text-xs">
                  <tbody>
                    {WHO.map(([name, what, tier]) => (
                      <tr key={name} className="border-t border-gray-100 align-top dark:border-slate-800">
                        <td className="w-36 py-1.5 pr-3 font-medium text-gray-900 dark:text-slate-100">{name}</td>
                        <td className="py-1.5 pr-3 text-gray-600 dark:text-slate-400">{what}</td>
                        <td className="w-24 py-1.5">
                          <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${tier === 'strong'
                            ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300'}`}>
                            {tier === 'strong' ? 'strong model' : 'fast model'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-slate-400">
                  Anything an affiliate reads, and the plan behind it, runs on the strong model. The agents that read a
                  lot and answer in a few structured fields run on the fast one — they also run the most often, so that
                  is where the spend would otherwise go. An admin can move any agent to any model in the User Panel, and
                  every run's tokens are recorded under <em>Activity</em>.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Outside the fold: folding the card away must not shut the walk-through. */}
      {tour && (
        <SetupTour
          status={{ mailboxes, readableMailbox, repliesReadable, brandChosen, dealsChosen, sendersChosen, editing: !!team }}
          onClose={() => setTour(false)}
        />
      )}
    </section>
  );
};

export default SetupGuide;
